import { useEffect, useRef } from "react";
import classNames from "classnames";
import Plyr from "plyr";
import { usePunditUserContext } from "@circle-react/contexts";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { AudioTranscriptions } from "./AudioTranscription";
import "./audio-player.scss";

export interface AudioPlayerProps {
  controls?: string[];
  downloadUrl?: string;
  isVoiceMessage?: boolean;
  src: string;
  transcriptionId?: number | null;
  transcriptionPreview?: string | null;
}

export const AudioPlayer = ({
  src,
  downloadUrl,
  controls = ["play", "progress", "current-time", "mute", "volume", "settings"],
  isVoiceMessage = false,
  transcriptionId,
  transcriptionPreview,
}: AudioPlayerProps) => {
  const { currentCommunityMember } = usePunditUserContext();
  const isAdmin = isCommunityAdmin(currentCommunityMember);
  const audioElementRef = useRef(null);
  const playerControls =
    isAdmin && !isVoiceMessage ? [...controls, "download"] : controls;

  useEffect(() => {
    if (!audioElementRef.current) {
      return;
    }

    const playerOptions = {
      controls: playerControls,
      invertTime: false,
      speed: {
        selected: 1,
        options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
      },
      storage: {
        enabled: false,
      },
      urls: {
        download: downloadUrl ?? src,
      },
    };

    const player = new Plyr(audioElementRef.current, playerOptions);

    return () => player.destroy();
  }, []);

  return (
    <div>
      <div
        className={classNames("post-audio-player", {
          "default mt-2": !isVoiceMessage,
          "voice-message": isVoiceMessage,
        })}
      >
        <audio ref={audioElementRef} preload="metadata" controls>
          <source src={src} />
        </audio>
      </div>
      {transcriptionId && transcriptionPreview && audioElementRef?.current && (
        <AudioTranscriptions
          transcriptionId={transcriptionId}
          transcriptionPreview={transcriptionPreview}
          audioPlayer={audioElementRef.current}
        />
      )}
    </div>
  );
};
